import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { SET_REGISTRY_ITEM_STATUS, SET_ATB_COMPONENT, SET_ATB_LOADING_STATE } from '~/types/mutations';
import { useAssociation } from '~/composables/useAssociation';
import { useQuickViewStore } from '~/stores/quickView';
import { useEnvPropsStore } from '~/stores/envProps';
import { useSlideoutStore } from '@nuxt-common/atomic-ui/app/stores/useSlideoutStore';
import useAddToBag from '~/composables/useAddToBag';

export const useQuickView = ({ propQuantityRequested, propQuantityPurchased, propIsRegistrant, propProduct, emits, propSlideoutId }) => {
  const { registryId, registryGUID } = useAssociation();
  const quickViewStore = useQuickViewStore();
  const envPropsStore = useEnvPropsStore();
  const slideoutStore = useSlideoutStore();
  const { addItemToBag } = useAddToBag();

  const route = useRoute();

  const disabledButtonText = ref('');
  const disableAddToBagCta = ref(false);
  const disablePurchaseQtyDD = ref(false);

  const requestedQuantity = computed(() => quickViewStore.registrantRequestedQty);
  const purchasedQuantity = computed(() => quickViewStore.registrantPurchasedQty);
  const selectedQuantity = computed(() => quickViewStore.itemQty);
  const productDetail = computed(() => quickViewStore.quickViewProductDetails);
  const productDetailMeta = computed(() => quickViewStore.quickViewProductDetailsMeta);
  const productDetailsApiErrorResponse = computed(() => quickViewStore.productDetailsApiErrorResponse);
  const selectedColor = computed(() => productDetail.value?.traits?.colors?.selectedColor?.upcKey);
  const selectedUpcId = computed(() => productDetail.value?.traits?.traitsMaps?.upcMap?.[selectedColor.value]);
  const defaultUpcDetails = computed(() => productDetail.value?.relationships?.upcs?.[selectedUpcId.value]);
  const upcDetails = computed(() => quickViewStore.upcDetails);
  const selectedSize = computed(() => productDetail.value?.traits?.sizes?.selectedSize);
  const isAddToBagLoading = computed(() => quickViewStore.quickViewLoadingProductId === propProduct.id);

  const regId = computed(() => {
    if (propIsRegistrant) {
      return registryId.value;
    }
    return route?.params?.registryId;
  });
  const errorState = computed(() => quickViewStore.errorState);
  const hasPurchasedItems = computed(() => propQuantityPurchased > 0);
  const hasRequestDropdownChanged = computed(() => propQuantityRequested !== requestedQuantity.value);
  const hasPurchasedDropdownChanged = computed(() => purchasedQuantity.value > propQuantityPurchased);
  const isColorOrSizeChanged = computed(
    () =>
      (Object.keys(upcDetails.value ? upcDetails.value : {}).length > 0 && defaultUpcDetails.value?.id !== upcDetails.value?.upcId) ||
      (quickViewStore.selectedSize && quickViewStore.selectedSize !== selectedSize.value) ||
      (quickViewStore.selectedColor && quickViewStore.selectedColor !== Number(selectedColor?.value?.split('_')[0])),
  );
  const itemFullyPurchased = computed(() => propQuantityPurchased >= propQuantityRequested);
  const upcsRefObj = computed(() => {
    // using this object untill the common component will emit the upc number on changing the color/size
    const upcIds = {};
    const upcs = productDetail.value?.relationships?.upcs;
    if (upcs) {
      Object.keys(upcs).forEach((upcId) => {
        upcIds[upcId] = upcs[upcId]?.identifier?.upcNumber;
      });
    }
    return upcIds;
  });
  const disableFields = computed(() => {
    const formFields = {
      color_size: false,
      requested: false,
      purchased: false,
      remove: false,
    };
    if (hasRequestDropdownChanged.value) {
      formFields.color_size = true;
      formFields.purchased = true;
    }
    if (hasPurchasedDropdownChanged.value) {
      formFields.color_size = true;
      formFields.requested = true;
    }
    if (isColorOrSizeChanged.value) {
      formFields.purchased = true;
      formFields.requested = true;
    }
    if (hasPurchasedItems.value) {
      // mutate the attribute when item is partially purchased, using attr to display copy above the Save CTA
      quickViewStore[SET_REGISTRY_ITEM_STATUS](formFields);
    }
    if (itemFullyPurchased.value) {
      formFields.remove = true;
    }
    return formFields;
  });
  const enableQuantityDropdown = computed(() => {
    if (propIsRegistrant) return 0;

    return propQuantityPurchased >= propQuantityRequested;
  });
  const isMew = computed(() => envPropsStore.envProps.isMew);
  const productImage = computed(() => propProduct?.imagery);

  const networkAvailability = propProduct?.networkAvailability || [];
  const isAvailable = ref(networkAvailability[0]?.available);
  const fulfillmentMethod = ref(networkAvailability[0]?.fulfillmentMethod);
  const discontinuedCode = ref(networkAvailability[0]?.discontinuedCode);

  if (itemFullyPurchased.value) {
    // State: Purchased/Completed
    // disabledButtonText.value = 'Fullfilled'; Waiting for confirmation
  } else if (!isAvailable.value && discontinuedCode.value?.toUpperCase() === 'Y') {
    // State: Discontinued out of stock
    disabledButtonText.value = 'Discontinued';
    disableAddToBagCta.value = true;
  } else if (!isAvailable.value && (!fulfillmentMethod.value?.includes('STH') || fulfillmentMethod.value?.length <= 0)) {
    // OOS for online, OOS for all channel
    disabledButtonText.value = 'Out of Stock';
    disablePurchaseQtyDD.value = true;
    disableAddToBagCta.value = true;
  }
  const saveChangedAPIResponse = computed(() => quickViewStore.registrantItemQuantitiesApiResponse);
  // methods

  const handleAddToBag = async () => {
    quickViewStore[SET_ATB_LOADING_STATE]({ source: 'QUICKVIEW', productId: propProduct.id });

    let upcId = propProduct?.detail?.upcId;
    let upcNumber = propProduct?.detail?.upcNumber;
    if (upcDetails.value?.upcId) {
      upcId = upcDetails.value?.upcId;
    } else if (defaultUpcDetails.value?.id) {
      upcId = defaultUpcDetails.value?.id;
    }

    if (productDetail.value?.detail?.upcNumber) {
      upcNumber = productDetail.value?.detail?.upcNumber;
    } else if (defaultUpcDetails.value?.identifier?.upcNumber) {
      upcNumber = defaultUpcDetails.value?.identifier?.upcNumber;
    }

    const payload = {
      item: {
        upcId,
        upcNumber,
        registryId: regId.value,
        quantity: selectedQuantity.value ? selectedQuantity.value : 1,
        source: 'REGA2B',
      },
    };

    return addItemToBag(payload)
      .then((atbConfirmation) => {
        slideoutStore.toggle(propSlideoutId, { root: true });
        quickViewStore[SET_ATB_COMPONENT](atbConfirmation.component);
      })
      .catch((error) => {
        const message = error?.cause?.message || error?.message;

        emits('registry-atb-error', message);
      })
      .finally(() => {
        quickViewStore[SET_ATB_LOADING_STATE]({ source: 'QUICKVIEW', productId: null });
      });
  };

  const toggleDeleteModalOverlay = (product) => {
    emits('toggleDeleteModalOverlay', product);
  };

  return {
    purchasedQuantity,
    requestedQuantity,
    selectedQuantity,
    productImage,
    productDetail,
    productDetailsApiErrorResponse,
    selectedColor,
    selectedUpcId,
    isAvailable,
    defaultUpcDetails,
    upcDetails,
    regId,
    registryId,
    registryGUID,
    hasPurchasedItems,
    hasRequestDropdownChanged,
    hasPurchasedDropdownChanged,
    isColorOrSizeChanged,
    itemFullyPurchased,
    disableFields,
    enableQuantityDropdown,
    errorState,
    upcsRefObj,
    isMew,
    saveChangedAPIResponse,
    handleAddToBag,
    toggleDeleteModalOverlay,
    productDetailMeta,
    disabledButtonText,
    disablePurchaseQtyDD,
    disableAddToBagCta,
    discontinuedCode,
    isAddToBagLoading,
  };
};
